import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import gql from 'graphql-tag';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import isNil from 'lodash-es/isNil';
import orderBy from 'lodash-es/orderBy';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { IGNORE_ALL_GRAPHQL_ERRORS } from 'src/app/_core/graphql.module';
import { ActivityPrintMailSelection } from 'src/app/_core/services/activity-print-mail.service';
import { GraphqlService } from 'src/app/_core/services/graphql.service';

import {
    EditerLigneCommandeBilletterieGestionModelXml,
    EditerLigneCommandeChangementStatutGestionModelXml,
    EditerLigneCommandeChangementStatutLocationGestionModel,
    EditerLigneCommandeLocationGestionModelXml,
    EditerLigneCommandeRetourPopupGestionModelXml,
    EditerLigneCommandeRetourPopupLocationGestionModel
} from 'src/app/board/activity/activity-shared/activity-beneficiaries-orders/pages/activity-new-sale/activity-new-sale/activity-new-sale-xml';
import {
    ActivitySettlementPreferencesService
} from 'src/app/board/activity/activity-shared/activity-beneficiaries-orders/services/activity-settlement-preferences.service';
import { AdministrativeStatus, PaymentMode, RouteActivityType } from 'src/app/board/activity/activity.constants';
import { ActivitySaleFragment } from 'src/app/board/activity/fragments/activity-sale.fragment';
import { ActivityNewSaleService } from 'src/app/board/activity/services/activity-new-sale.service';
import {
    ActivitySetBeneficiaryAction
} from 'src/app/board/activity/stores/activity-beneficiary/activity-beneficiary.action';
import {
    ActivityAddOrUpdateOrderLineAction,
    ActivityCancelEngineFeebackOnOrderLineAction,
    ActivityCheckSettlementPreferenceAction,
    ActivityClearOrderLinesListAction,
    ActivityDeleteOrderLineAction,
    ActivityDeleteSaleAction,
    ActivityGetActivityTypeCodeFromActivityIdAction,
    ActivityGetDeliveryModesListAction,
    ActivityGetFamiliesListAction,
    ActivityGetProductsListAction,
    ActivityInitializeSaleByIdOrderAction,
    ActivityInitializeSalesAction,
    ActivitySalesCanDeleteDepositAction,
    ActivitySalesChangeProcessAction,
    ActivitySalesDeleteDepositAction,
    ActivitySalesGetDepositsListAction,
    ActivitySalesPrepareOrderAction,
    ActivitySalesResetDepositsListAction,
    ActivitySalesSetPaymentMethodAction,
    ActivitySetActivityIdAction,
    ActivitySetArticlesCountAction,
    ActivitySetFiltersAction,
    ActivityToggleFiltersAction,
    ActivityUpdateChoicesSessionsAction,
    ActivityUpdateOrderHeaderAction,
    ActivityUpdateOrderLineStatusAction,
    ActivityUpdateSettlementPreferenceAction,
    FinalizeOrderAction,
    GetLastWritingAccountNumberAction,
    IgnoreKeepDialogAction,
    ResetActivitySalesStateAction,
    SetPrintsAndMailsActivitySalesAction,
    UpdateDevisBenefAction
} from 'src/app/board/activity/stores/activity-sales/activity-sales.action';
import { generateRawSoapXml } from 'src/app/utils/soap.utils';
import {
    AcompteModelDto,
    CriteresArticleDtoInput,
    FamilleProduitModel,
    LigneDevisModeleDto,
    MenuPageParamDto,
    ModeLivraisonModeleDto,
    MutationChangerStatutLigneCommandeGestionArgs,
    MutationDeleteLigneCommandeForVenteArgs,
    MutationEditerLigneCommandeGestionPlusArgs,
    MutationFinProcessVentesArgs,
    MutationGetCodeTypeActiviteWithIdActArgs,
    MutationGetDernierCompteEcritureNumeroArgs,
    MutationGetListeAcompteModelWithIdCdeArgs,
    MutationGetListeFacturesGeneriqueArgs,
    MutationGetListeFamilleproduitModelArgs,
    MutationGetListeModeLivraisonModelForVentesClientsArgs,
    MutationGetListeProduitModelArgs,
    MutationInitialisationByIdCommandeForVentesClientsArgs,
    MutationIsSupprimableFactureArgs,
    MutationPreparationResumeCommandeAvantReglementArgs,
    MutationSuppressionPhysiqueOuAnnulationListeDevisArgs,
    MutationSupprimerAcompteArgs,
    MutationUpdateChoixSessionChoixVentesClientArgs,
    MutationUpdateDevisBenefArgs,
    MutationUpdateEnteteDevisByModelArgs,
    PopupValiderCommandeModele,
    ProduitModel,
    RetourMoteurModelDto,
    TypeRetourMoteurEnum,
    VentesClientsModeleDto
} from 'src/graphql/generated-types';

export class ActivityPrintMailInformations {

    invoiceId?: number | null = null;

    invoiceNumber?: string | null = null;

    supplierInvoiceId?: number | null = null;

    supplierInvoiceNumber?: string | null = null;

    invoiceLabel? = 'Facture';

    deliveryVouchersId?: number[] = [];

    ordersId?: number[] = [];

    chequeLetterPrintId?: number | null = null;

    budgetCommitmentPrintId?: number | null = null;

    activityType?: string;

    printId?: number | null = null;

}

export enum SaleProcess {
    InProgress,
    Invoiced,
    Paid
}

export class ActivitySalesStateModel {

    loading = false;

    finalizeLoading = false;

    saleContext: VentesClientsModeleDto = null;

    activityId: number | null = null;

    deliveryModesList: ModeLivraisonModeleDto[] = [];

    familiesList: FamilleProduitModel[] = [];

    productsList: ProduitModel[] = [];

    filters: CriteresArticleDtoInput = {};

    isOpenedFilters = false;

    countListElements = 0;

    paymentContext: PopupValiderCommandeModele | null = null;

    selectedPaymentMethodId: number | null = null;

    selectedPaymentMode: PaymentMode = PaymentMode.None;

    printInfos: ActivityPrintMailInformations = new ActivityPrintMailInformations();

    mailOptions: ActivityPrintMailSelection | null = null;

    printOptions: ActivityPrintMailSelection | null = null;

    depositsList: AcompteModelDto[] = [];

    canDeleteDeposit = false;

    lineWaitingEngineFeedback: LigneDevisModeleDto | null = null;

    activityTypeCode: string | null = null;

    lastWritingAccountNumber = 0;

    invoiceId: number | null = null;

    currentProcess = SaleProcess.InProgress;

    isSettledByDeposit = false;

    ignoreKeepDialog = false;

}

@State<ActivitySalesStateModel>({
    name: 'activitySales',
    defaults: new ActivitySalesStateModel()
})
@Injectable()
export class ActivitySalesState {

    constructor(private readonly graphqlService: GraphqlService,
                private readonly activityNewSaleService: ActivityNewSaleService,
                private readonly activitySettlementPreferenceService: ActivitySettlementPreferencesService) {
    }

    // <editor-fold defaultstate="collapsed" desc="Selectors">

    @Selector([({menu})=> menu?.selectedMenu?.MenuPage?.ListeParametres?.MenuPageParamDTO || []])
    static currentActivityId(menuParamList: MenuPageParamDto[]): number {
        return +get(
            menuParamList.find(e => e.Libelle === 'ACT_ID'),
            'Valeur',
            null);
    }

    @Selector()
    public static ignoreKeepDialog(state: ActivitySalesStateModel): boolean {
        return state?.ignoreKeepDialog;
    }

    @Selector()
    public static loading(state: ActivitySalesStateModel): boolean {
        return state?.loading;
    }

    @Selector()
    public static selectedInvoiceId(state: ActivitySalesStateModel): number {
        return state?.invoiceId;
    }

    @Selector()
    public static finalizeLoading(state: ActivitySalesStateModel): boolean {
        return state?.finalizeLoading;
    }

    @Selector()
    public static activityTypeCode(state: ActivitySalesStateModel): string {
        return state?.activityTypeCode;
    }

    @Selector()
    public static orderId(state: ActivitySalesStateModel): number {
        return state?.saleContext?.EnteteDevisModel?.IdDevisCommande;
    }

    @Selector()
    public static saleContext(state: ActivitySalesStateModel): VentesClientsModeleDto {
        return state?.saleContext;
    }

    @Selector()
    public static paymentContext(state: ActivitySalesStateModel): PopupValiderCommandeModele {
        return state?.paymentContext;
    }

    @Selector()
    public static selectedPaymentMethodId(state: ActivitySalesStateModel): number {
        return state?.selectedPaymentMethodId;
    }

    @Selector()
    public static selectedPaymentMode(state: ActivitySalesStateModel): PaymentMode {
        return state?.selectedPaymentMode;
    }

    @Selector()
    public static deliveryModesList(state: ActivitySalesStateModel): ModeLivraisonModeleDto[] {
        return state?.deliveryModesList;
    }

    @Selector()
    public static familiesList(state: ActivitySalesStateModel): FamilleProduitModel[] {
        return state?.familiesList;
    }

    @Selector()
    public static productsList(state: ActivitySalesStateModel): ProduitModel[] {
        return state?.productsList;
    }

    @Selector()
    public static filters(state: ActivitySalesStateModel): any {
        return state?.filters;
    }

    @Selector()
    static isOpenedFilters(state: ActivitySalesStateModel): boolean {
        return state?.isOpenedFilters;
    }

    @Selector()
    static countListElements(state: ActivitySalesStateModel): number {
        return state?.countListElements;
    }

    @Selector()
    public static activityId(state: ActivitySalesStateModel): number {
        return state?.activityId;
    }

    @Selector()
    public static hasPrints(state: ActivitySalesStateModel): boolean {
        if (!state?.printOptions) {
            return false;
        }
        const {deliveryVouchers, orders, saleReceipts, invoice} = state.printOptions;
        return deliveryVouchers || orders || saleReceipts || invoice;
    }

    @Selector()
    public static hasMails(state: ActivitySalesStateModel): boolean {
        if (!state?.mailOptions) {
            return false;
        }
        const {deliveryVouchers, orders, saleReceipts, invoice, forms} = state.mailOptions;
        return deliveryVouchers || orders || saleReceipts || invoice || forms;
    }

    @Selector()
    public static printOptions(state: ActivitySalesStateModel): ActivityPrintMailSelection {
        return state?.printOptions;
    }

    @Selector()
    public static mailOptions(state: ActivitySalesStateModel): ActivityPrintMailSelection {
        return state?.mailOptions;
    }

    @Selector()
    public static printInfos(state: ActivitySalesStateModel): ActivityPrintMailInformations {
        return state?.printInfos;
    }

    @Selector()
    public static depositsList(state: ActivitySalesStateModel): AcompteModelDto[] {
        return state?.depositsList;
    }

    @Selector()
    public static lineWaitingEngineFeedback(state: ActivitySalesStateModel): LigneDevisModeleDto {
        return state?.lineWaitingEngineFeedback;
    }

    @Selector()
    public static lastWritingAccountNumber(state: ActivitySalesStateModel): number {
        return state?.lastWritingAccountNumber;
    }

    @Selector()
    public static currentProcess(state: ActivitySalesStateModel): SaleProcess {
        return state?.currentProcess;
    }

    @Selector()
    public static isSettledByDeposit(state: ActivitySalesStateModel): boolean {
        return state?.isSettledByDeposit;
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Actions">

    // <editor-fold defaultstate="collapsed" desc="Initialization">

    @Action(IgnoreKeepDialogAction)
    setIgnoreKeepOrderConfirmDialog({patchState}: StateContext<ActivitySalesStateModel>,{ignoreKeepDialog}: IgnoreKeepDialogAction): void {
        patchState({ignoreKeepDialog});
    }

    @Action(ActivitySetActivityIdAction)
    setActivityId({patchState}: StateContext<ActivitySalesStateModel>, {activityId}: ActivitySetActivityIdAction): void {
        patchState({activityId});
    }

    @Action(ActivityInitializeSalesAction)
    initializeSales({
                        getState,
                        patchState
                    }: StateContext<ActivitySalesStateModel>,
                    {params}: ActivityInitializeSalesAction): Observable<any> {
        patchState({loading: true});
        let action: Observable<VentesClientsModeleDto>;

        switch (params.activityType) {
            case RouteActivityType.Sales:
                action = this.activityNewSaleService.initSales({activityId: getState().activityId});
                break;
            case RouteActivityType.Groups:
                action = this.activityNewSaleService.initGroups({
                    activityId: getState().activityId,
                    productId: params.productId
                });
                break;
            case RouteActivityType.Rentals:
                action = this.activityNewSaleService.initRentals({
                    activityId: getState().activityId,
                    articleId: params.articleId
                });
                break;
        }

        if(!action) {
            return of(null);
        }

        return action.pipe(
            tap((InitialisationForVentesClients) => {
                InitialisationForVentesClients.ListeLigneDevisModel = {LigneDevisModeleDTO: []};
                patchState({
                    loading: false,
                    saleContext: InitialisationForVentesClients,
                });
            }),
            catchError(err => {
                patchState({loading: false});
                return throwError(err);
            }),
        );
    }

    @Action(ActivityInitializeSaleByIdOrderAction)
    initializeSaleByIdOrder({
                                getState,
                                patchState
                            }: StateContext<ActivitySalesStateModel>,
                            {orderId, activityId}: ActivityInitializeSaleByIdOrderAction): Observable<any> {
        patchState({loading: true});
        return this.graphqlService.mutateOne<'InitialisationByIdCommandeForVentesClients', MutationInitialisationByIdCommandeForVentesClientsArgs>
        ({
            mutation: gql`
                mutation InitialisationByIdCommandeForVentesClients($contexte: ContextInfosInput, $initModel: InitModeleForVenteInput){
                    InitialisationByIdCommandeForVentesClients(
                        contexte: $contexte
                        initModel: $initModel
                    ){
                        ...ventesClientModel
                    }
                }
                ${ActivitySaleFragment.ventesClientModelFragment}
            `,
            variables: {
                initModel: this.activityNewSaleService.initModel({
                    activityId,
                    orderId
                })
            }
        })
            .pipe(
                tap((InitialisationByIdCommandeForVentesClients) => {
                    patchState({
                        loading: false,
                        saleContext: InitialisationByIdCommandeForVentesClients
                    });
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                }),
            );
    }

    @Action(ActivityGetActivityTypeCodeFromActivityIdAction)
    getActivityTypeCodeFromActivityId({
                                          getState,
                                          patchState
                                      }: StateContext<ActivitySalesStateModel>,
                                      {activityId}: ActivityGetActivityTypeCodeFromActivityIdAction): Observable<any> {
        patchState({loading: true});

        return this.graphqlService.mutateOne<'GetCodeTypeActiviteWithIdAct', MutationGetCodeTypeActiviteWithIdActArgs>
        ({
            mutation: gql`
                mutation GetCodeTypeActiviteWithIdAct($contexte: ContextInfosInput, $idActivite: Float){
                    GetCodeTypeActiviteWithIdAct(
                        contexte: $contexte
                        idActivite: $idActivite
                    )
                }
            `,
            variables: {
                idActivite: activityId
            }
        })
            .pipe(
                tap((GetCodeTypeActiviteWithIdAct) => {
                    patchState({
                        loading: false,
                        activityTypeCode: GetCodeTypeActiviteWithIdAct
                    });
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                }),
            );
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="DeliveryModes">

    @Action(ActivityGetDeliveryModesListAction)
    getDeliveryModesList({patchState}: StateContext<ActivitySalesStateModel>): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeModeLivraisonModelForVentesClients', MutationGetListeModeLivraisonModelForVentesClientsArgs>
        ({
            mutation: gql`
                mutation GetListeModeLivraisonModelForVentesClients($contexte: ContextInfosInput){
                    GetListeModeLivraisonModelForVentesClients(contexte: $contexte) {
                        ModeLivraisonModeleDTO {
                            IdModeLivraison
                            CodeModeLivraison
                            LibelleModeLivraison
                            IsClos
                        }
                    }
                }
            `,
        })
            .pipe(
                tap((GetListeModeLivraisonModelForVentesClients) => {
                    patchState({
                        deliveryModesList: GetListeModeLivraisonModelForVentesClients?.ModeLivraisonModeleDTO || [],
                    });
                }),
                catchError(err => {
                    return throwError(err);
                }),
            );
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Order">

    @Action(UpdateDevisBenefAction)
    updateDevisBenef({getState, patchState, dispatch}: StateContext<ActivitySalesStateModel>, {
        idBeneficiary,
        orderId
    }: UpdateDevisBenefAction): Observable<any> {
        patchState({loading: true});

        return this.graphqlService.mutateOne<'UpdateDevisBenef', MutationUpdateDevisBenefArgs>
        ({
            mutation: gql`
                mutation UpdateDevisBenef($contexte: ContextInfosInput, $idDevis: Float, $IdBenef: Float){
                    UpdateDevisBenef(
                        contexte: $contexte,
                        idDevis: $idDevis,
                        IdBenef: $IdBenef
                    ){
                        IdBenef
                        NomBenef
                        PrenomBenef
                        Matricule
                        UrlPictoBenef
                        IsBlocageDroitActif
                        IsDroitsActifs
                        IdDevis
                    }
                }
            `,
            variables: {
                IdBenef: idBeneficiary,
                idDevis: orderId
            }
        })
            .pipe(
                tap((UpdateDevisBenef) => {
                        const saleContext = cloneDeep(getState().saleContext);
                        saleContext.EnteteDevisModel.IdDevisCommande = UpdateDevisBenef.IdDevis;
                        saleContext.sessionChoixModel = null;

                        dispatch(new ActivitySetBeneficiaryAction(UpdateDevisBenef));

                        patchState({
                            saleContext,
                            loading: false,
                        });
                    },
                    catchError(err => {
                        patchState({loading: false});
                        return throwError(err);
                    }))
            );
    }

    @Action(ActivityDeleteSaleAction)
    deleteSale({getState, patchState}: StateContext<ActivitySalesStateModel>): Observable<any> {
        patchState({loading: true});

        if (isNil(getState().saleContext)) {
            patchState({loading: false});
            return;
        }

        return this.graphqlService.mutateOne<'SuppressionPhysiqueOuAnnulationListeDevis', MutationSuppressionPhysiqueOuAnnulationListeDevisArgs>
        ({
            mutation: gql`
                mutation SuppressionPhysiqueOuAnnulationListeDevis($contexte: ContextInfosInput, $ListIdDevisCommande: ArrayOfdecimalInput){
                    SuppressionPhysiqueOuAnnulationListeDevis(
                        contexte: $contexte
                        ListIdDevisCommande: $ListIdDevisCommande
                    )
                }
            `,
            variables: {
                ListIdDevisCommande: {decimal: [getState().saleContext.EnteteDevisModel.IdDevisCommande]}
            }
        })
            .pipe(
                tap(() => {
                    patchState({
                        loading: false,
                        saleContext: null,
                    });
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                }),
            );
    }

    @Action(ActivityUpdateOrderHeaderAction)
    updateOrderHeader({
                          getState,
                          patchState
                      }: StateContext<ActivitySalesStateModel>,
                      {header}: ActivityUpdateOrderHeaderAction): Observable<any> {
        patchState({loading: true});

        return this.graphqlService.mutateOne<'UpdateEnteteDevisByModel', MutationUpdateEnteteDevisByModelArgs>
        ({
            mutation: gql`
                mutation UpdateEnteteDevisByModel($contexte: ContextInfosInput, $enteteDeivsModel: EnteteDevisCommandeForVentesModeleDTOInput){
                    UpdateEnteteDevisByModel(
                        contexte: $contexte
                        enteteDeivsModel: $enteteDeivsModel
                    )
                }
            `,
            variables: {
                enteteDeivsModel: header || getState().saleContext.EnteteDevisModel
            }
        })
            .pipe(
                tap(() => {

                    const saleContext = cloneDeep(getState().saleContext);
                    saleContext.EnteteDevisModel = header || saleContext.EnteteDevisModel;
                    patchState({
                        loading: false,
                        saleContext
                    });
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                }),
            );
    }

    @Action(ActivityAddOrUpdateOrderLineAction)
    addOrUpdateOrderLine({
                             getState,
                             patchState
                         }: StateContext<ActivitySalesStateModel>, {params}: ActivityAddOrUpdateOrderLineAction): Observable<any> {
        // Si different de null, un ajout de ligne avec un retour moteur n'a pas été traité
        if (getState().lineWaitingEngineFeedback != null) {
            throw new Error('line waiting engine feedback');
        }

        let line = null;
        const isRentals = params.activityType === RouteActivityType.Rentals;

        if (params.isEngineFeedback) {
            line = isRentals ? EditerLigneCommandeRetourPopupLocationGestionModel(params.article) : EditerLigneCommandeRetourPopupGestionModelXml(params.article);
        } else {
            line = isRentals ? EditerLigneCommandeLocationGestionModelXml(params.article) : EditerLigneCommandeBilletterieGestionModelXml(params.article);
        }

        patchState({loading: true});

        return this.graphqlService.mutateOne<'EditerLigneCommandeGestionPlus', MutationEditerLigneCommandeGestionPlusArgs>
        ({
            mutation: gql`
                mutation EditerLigneCommandeGestionPlus(
                    $contexte: ContextInfosInput,
                    $rawXML: String
                ){
                    EditerLigneCommandeGestionPlus(
                        contexte: $contexte
                        rawXML: $rawXML
                    ) {
                        ...ligneDevisModel
                    }
                }
                ${ActivitySaleFragment.ligneDevisModelFragment}
            `,
            variables: {
                rawXML: line
            },
            context: {
                [IGNORE_ALL_GRAPHQL_ERRORS]: true
            }
        })
            .pipe(
                tap((EditerLigneCommandeGestionPlus) => {

                    if (this.isAddedLineAccepted(EditerLigneCommandeGestionPlus.RetourMoteurModel)) {
                        const saleContext = cloneDeep(getState().saleContext);

                        const orderLinesList = saleContext.ListeLigneDevisModel?.LigneDevisModeleDTO || [];
                        const indexOrderLine = orderLinesList.findIndex(e => e.IdLigneDevisCommande === EditerLigneCommandeGestionPlus.IdLigneDevisCommande);

                        if (!saleContext.ListeLigneDevisModel) {
                            saleContext.ListeLigneDevisModel = {LigneDevisModeleDTO: []};
                        }

                        if (indexOrderLine > -1) {
                            orderLinesList.splice(indexOrderLine, 1, EditerLigneCommandeGestionPlus);
                        } else {
                            orderLinesList.push(EditerLigneCommandeGestionPlus);
                        }

                        saleContext.EnteteDevisModel = EditerLigneCommandeGestionPlus.EnteteDevisModel;
                        saleContext.ListeLigneDevisModel.LigneDevisModeleDTO = orderLinesList;
                        saleContext.sessionChoixModel = EditerLigneCommandeGestionPlus.SessionChoixModel;

                        if (!EditerLigneCommandeGestionPlus.IsOptionLocation && !!EditerLigneCommandeGestionPlus.LibelleOptionAjoute) {
                            this.activityNewSaleService.checkOrderLineOptionLabel(EditerLigneCommandeGestionPlus.LibelleOptionAjoute);
                        }

                        patchState({saleContext});
                    } else {
                        patchState({
                            lineWaitingEngineFeedback: EditerLigneCommandeGestionPlus
                        });
                    }

                    patchState({loading: false});
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                })
            );
    }


    @Action(ActivityCancelEngineFeebackOnOrderLineAction)
    cancelEnginFeebackOnOrderLine({patchState}: StateContext<ActivitySalesStateModel>): void {
        patchState({lineWaitingEngineFeedback: null});
    }

    @Action(ActivityUpdateOrderLineStatusAction)
    updateOrderLineStatus(
        {getState, patchState}: StateContext<ActivitySalesStateModel>,
        {params}: ActivityUpdateOrderLineStatusAction): Observable<any> {

        patchState({loading: true});

        let result = null;

        if (params.activityType === RouteActivityType.Rentals) {
            result = EditerLigneCommandeChangementStatutLocationGestionModel({
                line: params.orderLine,
                isEngineFeedback: params.isEngineFeedback
            });
        } else {
            result = EditerLigneCommandeChangementStatutGestionModelXml({
                line: params.orderLine,
                isEngineFeedback: params.isEngineFeedback,
            });
        }

        return this.graphqlService.mutateOne<'ChangerStatutLigneCommandeGestion', MutationChangerStatutLigneCommandeGestionArgs>
        ({
            mutation: gql`
                mutation ChangerStatutLigneCommandeGestion($contexte: ContextInfosInput, $rawXML: String) {
                    ChangerStatutLigneCommandeGestion(contexte: $contexte, rawXML: $rawXML) {
                        CodeStatutAdmin
                        SubUnitaireAccepte
                        SubUnitaireTheorique
                        RetourMoteurModel {
                            ...retourMoteurModel
                        }
                        LibelleOptionAjoute
                    }
                }
                ${ActivitySaleFragment.retourMoteurFragment}
            `,
            variables: {
                rawXML: result
            }
        })
            .pipe(
                tap((ChangerStatutLigneCommandeGestion) => {
                    const model = ChangerStatutLigneCommandeGestion;

                    const saleContext = cloneDeep(getState().saleContext);
                    const selectedLine: LigneDevisModeleDto = saleContext.ListeLigneDevisModel.LigneDevisModeleDTO.find(l => {
                        return l.IdLigneDevisCommande === params.orderLine.IdLigneCommande;
                    });
                    selectedLine.Quantite = params.orderLine.Quantite;
                    const engineFeedBack = model.RetourMoteurModel;
                    const isAccepted = (!engineFeedBack || !engineFeedBack.Type || engineFeedBack.Type === TypeRetourMoteurEnum.Accepte);

                    selectedLine.CodeHistoStatut = model.CodeStatutAdmin;
                    selectedLine.CodeStatutLigne = model.CodeStatutAdmin;
                    selectedLine.RetourMoteurModel = engineFeedBack;

                    if ([AdministrativeStatus.Canceled, AdministrativeStatus.Refused].every(c => c !== selectedLine.CodeHistoStatut)) {
                        selectedLine.SubUnitaire = model.SubUnitaireAccepte || 0;
                        selectedLine.SubUnitTheorique = model.SubUnitaireTheorique || 0;
                        selectedLine.SubTotale = model.SubUnitaireAccepte || 0;
                        selectedLine.SubTotaleTheorique = model.SubUnitaireTheorique || 0;
                        selectedLine.PVUSubDeduite = selectedLine.PVU - selectedLine.SubUnitaire;
                        selectedLine.APayer = selectedLine.PVUSubDeduite * (selectedLine.Quantite === 0 ? 1 : selectedLine.Quantite);
                    } else {
                        selectedLine.SubUnitaire = 0;
                        selectedLine.SubUnitTheorique = 0;
                        selectedLine.SubTotale = 0;
                        selectedLine.SubTotaleTheorique = 0;
                        selectedLine.PVUSubDeduite = selectedLine.PVU;
                        selectedLine.APayer = 0;
                    }

                    if (params.activityType === RouteActivityType.Rentals && model.LibelleOptionAjoute) {
                        this.activityNewSaleService.checkOrderLineOptionLabel(model.LibelleOptionAjoute);
                    }

                    patchState({
                        saleContext,
                        lineWaitingEngineFeedback: !isAccepted ? selectedLine : null,
                        loading: false,
                    });
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                }),
            );
    }

    @Action(ActivityDeleteOrderLineAction)
    deleteOrderLine({
                        getState,
                        patchState
                    }: StateContext<ActivitySalesStateModel>,
                    {lineId}: ActivityDeleteOrderLineAction): Observable<any> {

        patchState({loading: true});

        return this.graphqlService.mutateOne<'DeleteLigneCommandeForVente', MutationDeleteLigneCommandeForVenteArgs>
        ({
            mutation: gql`
                mutation DeleteLigneCommandeForVente($contexte: ContextInfosInput, $IdLigne: Float) {
                    DeleteLigneCommandeForVente(contexte: $contexte, IdLigne: $IdLigne)
                }
            `,
            variables: {
                IdLigne: lineId
            }
        })
            .pipe(
                tap((DeleteLigneCommandeForVente) => {
                    const newOrderObject = DeleteLigneCommandeForVente;

                    const saleContext = cloneDeep(getState().saleContext);
                    const selectedLineIndex = saleContext.ListeLigneDevisModel.LigneDevisModeleDTO.findIndex(l => {
                        return l.IdLigneDevisCommande === lineId;
                    });

                    if (newOrderObject) {
                        saleContext.EnteteDevisModel.ObjetDevisCommande = newOrderObject;
                    }

                    saleContext.ListeLigneDevisModel.LigneDevisModeleDTO.splice(selectedLineIndex, 1);

                    patchState({
                        saleContext,
                        loading: false
                    });
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                }),
            );
    }

    @Action(ActivityClearOrderLinesListAction)
    clearOrderLinesList({getState, patchState}: StateContext<ActivitySalesStateModel>): void {
        const saleContext = cloneDeep(getState().saleContext);
        saleContext.ListeLigneDevisModel = {LigneDevisModeleDTO: []};

        patchState({saleContext});
    }

    @Action(ActivityUpdateSettlementPreferenceAction)
    updateSettlementsPreferences({
                                     getState,
                                     patchState
                                 }: StateContext<ActivitySalesStateModel>,
                                 {settlementPreference}: ActivityUpdateSettlementPreferenceAction): void {
        const saleContext = cloneDeep(getState().saleContext);
        saleContext.ModalitesReglementAffichage = settlementPreference;

        patchState({saleContext});
    }

    @Action(ActivityUpdateChoicesSessionsAction)
    updateChoicesSessions(
        {getState, patchState}: StateContext<ActivitySalesStateModel>,
        {choiceSession}: ActivityUpdateChoicesSessionsAction): Observable<any> {
        return this.graphqlService.mutateOne<'UpdateChoixSessionChoixVentesClient', MutationUpdateChoixSessionChoixVentesClientArgs>({
            mutation: gql`
                    mutation UpdateChoixSessionChoixVentesClient(
                        $contexte: ContextInfosInput,
                        $idCommande: Float,
                        $numeroChoix: Int
                    ) {
                        UpdateChoixSessionChoixVentesClient(
                            contexte: $contexte
                            idCommande: $idCommande
                            numeroChoix: $numeroChoix
                        ) {
                            IdSession
                            Libelle
                            NumPremierChoixDispo
                            ListNumeroChoix {
                                NumeroChoixModelDTO {
                                    Numero
                                    LibelleCMD
                                    IdCommandeConcernee
                                    IdCommandeEnCours
                                }
                            }
                        }
                    }
                `,
            variables: {
                idCommande: getState().saleContext.EnteteDevisModel.IdDevisCommande,
                numeroChoix: choiceSession.Numero
            }
        }).pipe(
            tap(UpdateChoixSessionChoixVentesClient => {
                const saleContext = cloneDeep(getState().saleContext);

                saleContext.sessionChoixModel = {
                    ...saleContext.sessionChoixModel,
                    ...UpdateChoixSessionChoixVentesClient
                };

                if (saleContext.IsLocation) {
                    saleContext.EnteteDevisModel = {
                        ...saleContext.EnteteDevisModel,
                        NumChoixLocation: choiceSession.Numero
                    }
                }

                patchState({
                    saleContext
                });
            })
        );
    }

    @Action(ActivityCheckSettlementPreferenceAction)
    checkSettlementPreference(
        {getState, patchState}: StateContext<ActivitySalesStateModel>,
        {params}: ActivityCheckSettlementPreferenceAction): Observable<any> {
        return this.activitySettlementPreferenceService.hasProductSettlementPreferences$(params.activityType, params.orderId).pipe(
            tap(e => {
                const saleContext = cloneDeep(getState().saleContext);
                saleContext.HasProduitWithModalitesReglement = e;
                patchState({saleContext});
            })
        );
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="FPA">

    @Action(ActivitySetArticlesCountAction)
    setArticlesCount({
                         getState,
                         patchState
                     }: StateContext<ActivitySalesStateModel>,
                     {count}: ActivitySetArticlesCountAction): void {
        patchState({countListElements: count});
    }

    @Action(ActivityGetFamiliesListAction)
    getFamiliesList({getState, patchState}: StateContext<ActivitySalesStateModel>, {activityId}: ActivityGetFamiliesListAction): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeFamilleproduitModel', MutationGetListeFamilleproduitModelArgs>({
            mutation: gql`
                mutation GetListeFamilleproduitModel($contexte: ContextInfosInput, $criteres: CriteresFamilleProduitDTOInput) {
                    GetListeFamilleproduitModel(contexte: $contexte, criteres: $criteres) {
                        FamilleProduitModel {
                            ID
                            Libelle
                        }
                    }
                }
            `,
            variables: {criteres: {IdActivite: activityId}}
        })
            .pipe(
                tap((GetListeFamilleproduitModel) => {
                    patchState({familiesList: GetListeFamilleproduitModel?.FamilleProduitModel || []});
                }),
                catchError(err => {
                    return throwError(err);
                })
            );
    }

    @Action(ActivityGetProductsListAction)
    getProductsList({
                        getState,
                        patchState
                    }: StateContext<ActivitySalesStateModel>,
                    {familyId}: ActivityGetProductsListAction): Observable<any> {
        return this.graphqlService.mutateOne<'GetListeProduitModel', MutationGetListeProduitModelArgs>({
            mutation: gql`
                mutation GetListeProduitModel($contexte: ContextInfosInput, $critere: CriteresProduitDTOInput) {
                    GetListeProduitModel(contexte: $contexte, critere: $critere) {
                        ProduitModel {
                            ID
                            Libelle
                        }
                    }
                }
            `,
            variables: {
                critere: {IdActivite: getState().activityId, IdFamille: familyId || null}
            }
        })
            .pipe(
                tap((GetListeProduitModel) => {
                    patchState({productsList: GetListeProduitModel?.ProduitModel || []});
                }),
                catchError(err => {
                    return throwError(err);
                })
            );
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Filters">

    @Action(ActivityToggleFiltersAction)
    toggleFilters(
        {getState, patchState}: StateContext<ActivitySalesStateModel>,
        {isOpenedFilters}: ActivityToggleFiltersAction
    ): void {
        patchState({
            isOpenedFilters: isOpenedFilters !== undefined ? isOpenedFilters : !getState().isOpenedFilters
        });
    }

    @Action(ActivitySetFiltersAction)
    setFilters(
        {patchState}: StateContext<ActivitySalesStateModel>,
        {filters}: ActivitySetFiltersAction
    ): void {
        patchState({
            filters
        });
    }

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="End sale">

    @Action(GetLastWritingAccountNumberAction)
    getLastWritingAccountNumber({
                                    getState,
                                    patchState
                                }: StateContext<ActivitySalesStateModel>,
                                {criteres}: GetLastWritingAccountNumberAction): Observable<any> {
        return this.graphqlService.mutateOne<'getDernierCompteEcritureNumero', MutationGetDernierCompteEcritureNumeroArgs>
        ({
            mutation: gql`
                mutation getDernierCompteEcritureNumero($contexte: ContextInfosInput, $critere: CritereRechercheNumeroPieceDTOInput){
                    getDernierCompteEcritureNumero(
                        contexte: $contexte,
                        critere: $critere
                    )
                }
            `,
            variables: {
                critere: criteres
            }
        })
            .pipe(
                tap((getDernierCompteEcritureNumero) => {
                    patchState({
                        lastWritingAccountNumber: getDernierCompteEcritureNumero ? +getDernierCompteEcritureNumero : 0
                    });
                })
            );
    }

    @Action(ActivitySalesSetPaymentMethodAction)
    setPaymentMethod({getState, patchState}: StateContext<ActivitySalesStateModel>, {
        payload: {
            paymentMethodId,
            paymentMode
        }
    }: ActivitySalesSetPaymentMethodAction): void {
        patchState({
            selectedPaymentMethodId: paymentMethodId || null,
            selectedPaymentMode: paymentMode || PaymentMode.None
        });
    }

    @Action(ActivitySalesPrepareOrderAction)
    prepareOrder({getState, patchState}: StateContext<ActivitySalesStateModel>): Observable<any> {
        patchState({loading: true});

        return this.graphqlService.mutateOne<'PreparationResumeCommandeAvantReglement', MutationPreparationResumeCommandeAvantReglementArgs>
        ({
            mutation: gql`
                mutation PreparationResumeCommandeAvantReglement(
                    $contexte: ContextInfosInput,
                    $ListIdCommande: ArrayOfdecimalInput,
                    $MultiBenef: Boolean
                ) {
                    PreparationResumeCommandeAvantReglement(
                        contexte: $contexte,
                        ListIdCommande: $ListIdCommande,
                        MultiBenef: $MultiBenef
                    ){
                        ListExercice{
                            ExerciceDTO{
                                Id
                                Code
                                DateDeb
                                DateFin
                            }
                        }
                        ListBanque{
                            string
                        }
                        ListeJournaux{
                            JournalDTO{
                                Code
                                Libelle
                                Id
                            }
                        }
                        ModeleFacturation{
                            IdBenef
                            LibelleFacture
                            DateFacture
                            IdJournal
                            IdExercice
                            NumCheque
                            Banque
                            NomTire
                        }
                        ListLigneDevisCommandeModel{
                            LigneDevisModeleDTO{
                                ...ligneDevisModel
                            }
                        }
                    }
                }
                ${ActivitySaleFragment.ligneDevisModelFragment}
            `,
            variables: {
                ListIdCommande: {decimal: [getState().saleContext.EnteteDevisModel.IdDevisCommande]},
                MultiBenef: false
            }
        })
            .pipe(
                tap((PreparationResumeCommandeAvantReglement) => {
                    const paymentContext = cloneDeep(PreparationResumeCommandeAvantReglement);
                    paymentContext.ListExercice.ExerciceDTO = orderBy(paymentContext.ListExercice.ExerciceDTO, ['Code'], ['desc']);

                    patchState({
                        paymentContext,
                        loading: false
                    });
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                }));
    }

    @Action(FinalizeOrderAction)
    finalizeOrder({
                      getState,
                      patchState,
                      dispatch
                  }: StateContext<ActivitySalesStateModel>,
                  {infosBilling}: FinalizeOrderAction): Observable<any> {
        patchState({finalizeLoading: true});

        return this.graphqlService.mutateOne<'FinProcessVentes', MutationFinProcessVentesArgs>
        ({
            mutation: gql`
                mutation FinProcessVentes($contexte: ContextInfosInput, $rawXML: String) {
                    FinProcessVentes(contexte: $contexte, rawXML: $rawXML){
                        IdFacture
                        ListeIdBonLivraison {
                            decimal
                        }
                        ListeIdCommande {
                            decimal
                        }
                        IsSoldeParAcompte
                    }
                }
            `,
            variables: {
                rawXML: generateRawSoapXml('model', infosBilling)
            }
        }).pipe(
            switchMap(({IdFacture, ListeIdBonLivraison, ListeIdCommande, IsSoldeParAcompte}) => {
                return this.getInvoiceNumber(IdFacture).pipe(
                    tap(invoiceNumber => {
                        patchState({
                            invoiceId: IdFacture,
                            finalizeLoading: false,
                            isSettledByDeposit: IsSoldeParAcompte,
                            currentProcess: getState().selectedPaymentMode === PaymentMode.AdvancedMode ? SaleProcess.Invoiced : SaleProcess.Paid,
                            printInfos: {
                                invoiceId: IdFacture,
                                invoiceNumber,
                                deliveryVouchersId: get(ListeIdBonLivraison, 'decimal', []),
                                ordersId: get(ListeIdCommande, 'decimal', []),
                            }
                        });
                    })
                );
            }),
            catchError(err => {
                patchState({finalizeLoading: false});
                return throwError(err);
            })
        );
    }

    @Action(ActivitySalesChangeProcessAction)
    changeProcess({patchState}: StateContext<ActivitySalesStateModel>, {process}: ActivitySalesChangeProcessAction): void {
        patchState({currentProcess: process});
    }

    // </editor-fold>

    // <editor-fold default-state="collapsed" desc="Has Prints">

    @Action(SetPrintsAndMailsActivitySalesAction)
    setHasPrints({patchState}: StateContext<ActivitySalesStateModel>, {
        options: {
            printOptions,
            mailOptions
        }
    }: SetPrintsAndMailsActivitySalesAction): void {
        patchState({mailOptions, printOptions});
    }

    // </editor-fold>

    @Action(ActivitySalesGetDepositsListAction)
    getDepositsList({getState, patchState}: StateContext<ActivitySalesStateModel>, {orderId}: ActivitySalesGetDepositsListAction): Observable<any> {

        return this.graphqlService.mutateOne<'GetListeAcompteModelWithIdCde', MutationGetListeAcompteModelWithIdCdeArgs>({
            mutation: gql`
                mutation GetListeAcompteModelWithIdCde($contexte: ContextInfosInput, $idCde: Float) {
                    GetListeAcompteModelWithIdCde(contexte: $contexte, idCde: $idCde){
                        AcompteModelDTO {
                            IdAcompte
                            Libelle
                            Montant
                            MoyenPayementModel {
                                Libelle
                            }
                            JournalModel {
                                LibelleJournal
                            }
                        }
                    }
                }
            `,
            variables: {
                idCde: orderId || getState().saleContext?.EnteteDevisModel?.IdDevisCommande
            }
        })
            .pipe(
                tap((GetListeAcompteModelWithIdCde) => {
                    patchState({depositsList: GetListeAcompteModelWithIdCde?.AcompteModelDTO || []});
                }),
                catchError(err => {
                    return throwError(err);
                })
            );
    }

    @Action(ActivitySalesCanDeleteDepositAction)
    canDeleteDeposit({
                         getState,
                         patchState
                     }: StateContext<ActivitySalesStateModel>,
                     {depositId}: ActivitySalesCanDeleteDepositAction): Observable<any> {

        patchState({loading: true});

        return this.graphqlService.mutateOne<'IsSupprimableFacture', MutationIsSupprimableFactureArgs>({
            mutation: gql`
                mutation IsSupprimableFacture($contexte: ContextInfosInput, $IdFac: Float) {
                    IsSupprimableFacture(contexte: $contexte, IdFac: $IdFac)
                }
            `,
            variables: {
                IdFac: depositId
            }
        })
            .pipe(
                tap((IsSupprimableFacture) => {
                    patchState({canDeleteDeposit: IsSupprimableFacture, loading: false});
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                })
            );
    }

    @Action(ActivitySalesDeleteDepositAction)
    deleteDeposit({
                      getState,
                      patchState
                  }: StateContext<ActivitySalesStateModel>,
                  {depositId}: ActivitySalesDeleteDepositAction): Observable<any> {

        patchState({loading: true});

        return this.graphqlService.mutateOne<'SupprimerAcompte', MutationSupprimerAcompteArgs>({
            mutation: gql`
                mutation SupprimerAcompte($contexte: ContextInfosInput, $IdFac: Float) {
                    SupprimerAcompte(contexte: $contexte, IdFac: $IdFac)
                }
            `,
            variables: {
                IdFac: depositId
            }
        })
            .pipe(
                tap((SupprimerAcompte) => {
                    if (SupprimerAcompte) {
                        const depositsList = cloneDeep(getState().depositsList);
                        const depositIndex = depositsList.findIndex(d => d.IdAcompte === depositId);
                        depositsList.splice(depositIndex, 1);

                        patchState({depositsList, loading: false});
                    }
                }),
                catchError(err => {
                    patchState({loading: false});
                    return throwError(err);
                })
            );
    }

    @Action(ActivitySalesResetDepositsListAction)
    resetDepositsList({patchState}: StateContext<ActivitySalesStateModel>): void {
        patchState({depositsList: []});
    }

    // <editor-fold defaultstate="collapsed" desc="Reset state">

    @Action(ResetActivitySalesStateAction)
    resetState({patchState}: StateContext<ActivitySalesStateModel>): void {
        patchState(new ActivitySalesStateModel());
    }

    // </editor-fold>

    // </editor-fold>

    // <editor-fold defaultstate="collapsed" desc="Privates">

    private isAddedLineAccepted(retour: RetourMoteurModelDto): boolean {
        return retour === null
            ||
            (
                get(retour, 'Accepte')
                && get(retour, 'Type') === TypeRetourMoteurEnum.Accepte
            );
    }

    private getInvoiceNumber(invoiceId): Observable<string> {
        return this.graphqlService.mutateOne<'GetListeFacturesGenerique', MutationGetListeFacturesGeneriqueArgs>({
            mutation: gql`
                mutation GetListeFacturesGenerique($contexte:ContextInfosInput,$criteres:CriteresFactureDTOInput){
                    GetListeFacturesGenerique(contexte:$contexte,criteres:$criteres){
                        FactureDTO{
                            NumFacture
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    Id: invoiceId,
                }
            }
        }).pipe(
            map((GetListeFacturesGenerique) => GetListeFacturesGenerique?.FactureDTO?.[0].NumFacture || '')
        );
    }

    // </editor-fold>
}
